/**
 * Leviathan - Quote Detail Page
 * Quasar Prime LLC
 *
 * View and manage sales quote details
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Edit,
  Send,
  Eye,
  Check,
  X,
  Copy,
  Loader2,
  Building2,
  User,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-500/20 text-slate-400 border-slate-500',
  SENT: 'bg-blue-500/20 text-blue-400 border-blue-500',
  VIEWED: 'bg-purple-500/20 text-purple-400 border-purple-500',
  ACCEPTED: 'bg-green-500/20 text-green-400 border-green-500',
  REJECTED: 'bg-red-500/20 text-red-400 border-red-500',
  EXPIRED: 'bg-orange-500/20 text-orange-400 border-orange-500',
  CONVERTED: 'bg-amber-500/20 text-amber-400 border-amber-500',
};

interface QuoteItem {
  id: string;
  productId: string | null;
  product: { id: string; name: string; sku: string } | null;
  description: string;
  sku: string | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountPercent: number;
  lineTotal: number;
  notes: string | null;
}

interface Quote {
  id: string;
  quoteNumber: string;
  title: string | null;
  description: string | null;
  status: string;
  version: number;
  validUntil: string | null;
  sentAt: string | null;
  viewedAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  subtotal: number;
  discountType: string | null;
  discountValue: number | null;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  deliveryFee: number;
  total: number;
  paymentTerms: string | null;
  deliveryTerms: string | null;
  specialTerms: string | null;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  lead: { id: string; leadNumber: string; companyName: string } | null;
  customer: { id: string; customerNumber: string; name: string } | null;
  items: QuoteItem[];
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Fetch quote
  const { data: quote, isLoading } = useQuery<Quote>({
    queryKey: ['quote', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch quote');
      return res.json();
    },
  });

  // Send quote mutation
  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to send quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
  });

  // Mark viewed mutation
  const viewMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/view`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to mark as viewed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
  });

  // Accept mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to accept quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (reason: string) => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error('Failed to reject quote');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      setShowRejectModal(false);
      setRejectReason('');
    },
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/quotes/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to duplicate quote');
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/sales/quotes/${data.id}`);
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isExpired = () => {
    if (!quote?.validUntil) return false;
    if (['ACCEPTED', 'REJECTED', 'EXPIRED', 'CONVERTED'].includes(quote.status)) return false;
    return new Date(quote.validUntil) < new Date();
  };

  const daysUntilExpiry = () => {
    if (!quote?.validUntil) return 0;
    const diff = new Date(quote.validUntil).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-300 mb-2">Quote not found</h3>
        <Link to="/sales/quotes" className="text-amber-400 hover:text-amber-300">
          Back to Quotes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/sales/quotes"
            className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-100">{quote.quoteNumber}</h1>
              <span className={`px-3 py-1 text-sm font-medium border rounded ${STATUS_COLORS[quote.status]}`}>
                {quote.status}
              </span>
              {quote.version > 1 && (
                <span className="px-2 py-1 text-xs font-medium bg-slate-700 text-slate-300 rounded">
                  v{quote.version}
                </span>
              )}
            </div>
            {quote.title && (
              <p className="text-slate-400 mt-1">{quote.title}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              Created {formatDate(quote.createdAt)}
              {isExpired() && (
                <span className="text-red-400 ml-4 flex items-center gap-1 inline-flex">
                  <AlertTriangle className="w-4 h-4" /> EXPIRED
                </span>
              )}
              {!isExpired() && daysUntilExpiry() <= 7 && daysUntilExpiry() > 0 && (
                <span className="text-amber-400 ml-4">
                  Expires in {daysUntilExpiry()} days
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {quote.status === 'DRAFT' && (
            <>
              <Link
                to={`/sales/quotes/${id}/edit`}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Link>
              <button
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sendMutation.isPending ? 'Sending...' : 'Mark Sent'}
              </button>
            </>
          )}

          {quote.status === 'SENT' && (
            <button
              onClick={() => viewMutation.mutate()}
              disabled={viewMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Eye className="w-4 h-4" />
              {viewMutation.isPending ? 'Updating...' : 'Mark Viewed'}
            </button>
          )}

          {['SENT', 'VIEWED'].includes(quote.status) && !isExpired() && (
            <>
              <button
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                {acceptMutation.isPending ? 'Accepting...' : 'Accept'}
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
            </>
          )}

          <button
            onClick={() => duplicateMutation.mutate()}
            disabled={duplicateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            <Copy className="w-4 h-4" />
            {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Quote Total</p>
              <p className="text-xl font-bold text-slate-100">{formatCurrency(quote.total)}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isExpired() ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Valid Until</p>
              <p className={`text-xl font-bold ${isExpired() ? 'text-red-400' : 'text-slate-100'}`}>
                {formatDate(quote.validUntil)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Line Items</p>
              <p className="text-xl font-bold text-slate-100">{quote.items.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20 text-green-400">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Payment Terms</p>
              <p className="text-xl font-bold text-slate-100">{quote.paymentTerms || 'Not specified'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Quote Info */}
        <div className="col-span-2 space-y-6">
          {/* Quote For */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
              Quote For
            </h3>
            {quote.lead && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-amber-400" />
                  <Link to={`/sales/leads/${quote.lead.id}`} className="text-amber-400 hover:text-amber-300 font-medium">
                    {quote.lead.companyName}
                  </Link>
                  <span className="text-slate-500 text-sm">({quote.lead.leadNumber})</span>
                </div>
              </div>
            )}
            {quote.customer && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-400" />
                  <Link to={`/customers/${quote.customer.id}`} className="text-amber-400 hover:text-amber-300 font-medium">
                    {quote.customer.name}
                  </Link>
                  <span className="text-slate-500 text-sm">({quote.customer.customerNumber})</span>
                </div>
              </div>
            )}
            {fullName({ firstName: quote.contactFirstName, lastName: quote.contactLastName }) && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Contact</p>
                <p className="text-slate-100">{fullName({ firstName: quote.contactFirstName, lastName: quote.contactLastName })}</p>
                {quote.contactEmail && <p className="text-slate-400 text-sm">{quote.contactEmail}</p>}
                {quote.contactPhone && <p className="text-slate-400 text-sm">{quote.contactPhone}</p>}
              </div>
            )}
            {quote.deliveryAddress && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Delivery Address</p>
                <p className="text-slate-100">{quote.deliveryAddress}</p>
                <p className="text-slate-400 text-sm">
                  {quote.deliveryCity}, {quote.deliveryState} {quote.deliveryZip}
                </p>
              </div>
            )}
          </div>

          {/* Line Items */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
              Line Items
            </h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left pb-3 text-xs text-slate-500 uppercase tracking-wider">Product</th>
                  <th className="text-right pb-3 text-xs text-slate-500 uppercase tracking-wider">Qty</th>
                  <th className="text-right pb-3 text-xs text-slate-500 uppercase tracking-wider">Unit Price</th>
                  <th className="text-right pb-3 text-xs text-slate-500 uppercase tracking-wider">Discount</th>
                  <th className="text-right pb-3 text-xs text-slate-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item) => (
                  <tr key={item.id} className="border-b border-slate-700/50">
                    <td className="py-3">
                      <div className="text-slate-100">{item.description}</div>
                      {item.product && (
                        <div className="text-slate-500 text-xs">{item.product.sku}</div>
                      )}
                      {item.notes && (
                        <div className="text-slate-500 text-xs mt-1">{item.notes}</div>
                      )}
                    </td>
                    <td className="py-3 text-right text-slate-100">
                      {item.quantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="py-3 text-right text-slate-100">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="py-3 text-right text-slate-400">
                      {item.discountPercent > 0 ? `${item.discountPercent}%` : '-'}
                    </td>
                    <td className="py-3 text-right text-amber-400 font-medium">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-700">
                  <td colSpan={4} className="py-2 text-right text-slate-400">Subtotal</td>
                  <td className="py-2 text-right text-slate-100">{formatCurrency(quote.subtotal)}</td>
                </tr>
                {quote.discountAmount > 0 && (
                  <tr>
                    <td colSpan={4} className="py-2 text-right text-slate-400">
                      Discount ({quote.discountType === 'PERCENT' ? `${quote.discountValue}%` : 'Fixed'})
                    </td>
                    <td className="py-2 text-right text-green-400">-{formatCurrency(quote.discountAmount)}</td>
                  </tr>
                )}
                {quote.taxAmount > 0 && (
                  <tr>
                    <td colSpan={4} className="py-2 text-right text-slate-400">
                      Tax ({quote.taxRate}%)
                    </td>
                    <td className="py-2 text-right text-slate-100">{formatCurrency(quote.taxAmount)}</td>
                  </tr>
                )}
                {quote.deliveryFee > 0 && (
                  <tr>
                    <td colSpan={4} className="py-2 text-right text-slate-400">Delivery Fee</td>
                    <td className="py-2 text-right text-slate-100">{formatCurrency(quote.deliveryFee)}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-amber-500">
                  <td colSpan={4} className="py-3 text-right text-slate-100 font-semibold uppercase">Total</td>
                  <td className="py-3 text-right text-amber-400 font-bold text-xl">{formatCurrency(quote.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Terms */}
          {(quote.deliveryTerms || quote.specialTerms) && (
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Terms & Conditions
              </h3>
              {quote.deliveryTerms && (
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Delivery Terms</p>
                  <p className="text-slate-100">{quote.deliveryTerms}</p>
                </div>
              )}
              {quote.specialTerms && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Special Terms</p>
                  <p className="text-slate-100 whitespace-pre-wrap">{quote.specialTerms}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Timeline & Notes */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
              Timeline
            </h3>
            <div className="space-y-4">
              <TimelineItem icon="pencil" label="Created" date={quote.createdAt} />
              {quote.sentAt && <TimelineItem icon="send" label="Sent" date={quote.sentAt} />}
              {quote.viewedAt && <TimelineItem icon="eye" label="Viewed" date={quote.viewedAt} />}
              {quote.acceptedAt && <TimelineItem icon="check" label="Accepted" date={quote.acceptedAt} highlight="success" />}
              {quote.rejectedAt && <TimelineItem icon="x" label="Rejected" date={quote.rejectedAt} highlight="error" />}
            </div>
          </div>

          {/* Description */}
          {quote.description && (
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Description
              </h3>
              <p className="text-slate-400 text-sm whitespace-pre-wrap">{quote.description}</p>
            </div>
          )}

          {/* Notes */}
          {quote.notes && (
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Customer Notes
              </h3>
              <p className="text-slate-400 text-sm whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}

          {/* Internal Notes */}
          {quote.internalNotes && (
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 pb-3 border-b border-slate-700">
                Internal Notes
              </h3>
              <p className="text-slate-400 text-sm whitespace-pre-wrap">{quote.internalNotes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">Reject Quote</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-400 mb-2">
                Reason for Rejection
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                placeholder="Why was this quote rejected?"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={() => rejectMutation.mutate(rejectReason)}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Reject Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineItem({
  icon,
  label,
  date,
  highlight
}: {
  icon: string;
  label: string;
  date: string;
  highlight?: 'success' | 'error';
}) {
  const icons: Record<string, string> = {
    pencil: '📝',
    send: '📤',
    eye: '👁',
    check: '✅',
    x: '❌',
  };

  const highlightColors = {
    success: 'text-green-400',
    error: 'text-red-400',
  };

  return (
    <div className="flex items-start gap-3">
      <span className="text-lg">{icons[icon] || '📌'}</span>
      <div>
        <div className={`font-medium ${highlight ? highlightColors[highlight] : 'text-slate-100'}`}>
          {label}
        </div>
        <div className="text-slate-500 text-xs">
          {new Date(date).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
